<template>
    <Teleport to="body">
        <div v-if="visible && hasData" class="news-windows-overlay" @click="handleOverlayClick">
            <div class="news-windows-container" @click.stop>
                <!-- 外框背景 -->
                <div class="popup-frame"
                    :style="{ backgroundImage: `url(${getAssetsFile('pop-upWindow/zujians.png')})` }">
                    <!-- 手风琴列表 -->
                    <div class="accordion-list">
                        <div v-for="(item, index) in displayAccordionData"
                            :key="`${index}-${expandedIndex}-${item.content}`" class="accordion-item"
                            :class="{ 'is-expanded': expandedIndex === index }" @click="toggleAccordion(index)">
                            <!-- 展开状态 -->
                            <div v-if="expandedIndex === index" class="accordion-content expanded"
                                :style="{ backgroundImage: `url(${getAssetsFile('pop-upWindow/ziyuan.png')})` }">
                                <div class="accordion-header">
                                    <img v-if="item.titleImage" :src="item.titleImage" alt="" class="title-image" />
                                    <h3 class="accordion-title">{{ item.title }}</h3>
                                </div>
                                <div class="accordion-body"
                                    @click.stop="item.jumpType && item.url ? handleJump(item) : null"
                                    :class="{ 'can-jump': item.jumpType && item.url }">
                                    <div class="accordion-text" :key="`content-${index}-${renderKey}`"
                                        v-html="item.content"></div>
                                </div>
                            </div>
                            <!-- 收起状态 -->
                            <div v-else class="accordion-content collapsed"
                                :style="{ backgroundImage: `url(${getAssetsFile('pop-upWindow/ziwei.png')})` }">
                                <div class="accordion-header">
                                    <img v-if="item.titleImage" :src="item.titleImage" alt="" class="title-image" />
                                    <h3 class="accordion-title">{{ item.title }}</h3>
                                    <img :src="getAssetsFile('pop-upWindow/jiantou.png')" alt="" class="arrow-icon" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 底部固定区域 -->
                    <div class="bottom-section"
                        :style="{ backgroundImage: `url(${getAssetsFile('pop-upWindow/erser.png')})` }">
                        <div class="bottom-content">
                            <div class="checkbox-wrapper" @click="toggleCheckbox">
                                <div class="checkbox-box"
                                    :style="{ backgroundImage: `url(${getAssetsFile('pop-upWindow/yuan.png')})` }">
                                    <img v-if="isChecked" :src="getAssetsFile('pop-upWindow/gou.png')" alt=""
                                        class="check-icon" />
                                </div>
                                <span class="checkbox-text">Don't remind me again</span>
                            </div>

                            <div class="close-button-wrapper" @click="handleClose">
                                <div class="close-button"
                                    :style="{ backgroundImage: `url(${getAssetsFile('pop-upWindow/butters.png')})` }">
                                    <span class="close-text">Close</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed, nextTick } from 'vue'
import { getAssetsFile } from '@/utils/pub-use'
import * as GameApi from '@/api/game'
import { useUserStore } from '@/stores/modules/user'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const router = useRouter()

const { locale } = useI18n()

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    accordionData: {
        type: Array,
        default: () => []
    },
    closeOnOverlay: {
        type: Boolean,
        default: true
    },
    preventScroll: {
        type: Boolean,
        default: true
    },
    autoFetch: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['close', 'update:visible'])

const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)

const expandedIndex = ref(0) // 默认展开第一个
const isChecked = ref(false)
const renderKey = ref(0) // 用于强制重新渲染富文本内容
const popupListData = ref([]) // 接口返回的原始数据
const currentPopupIndex = ref(0) // 当前显示的弹窗索引
const currentPopupData = ref(null) // 当前显示的弹窗数据
const processedAccordionData = ref([]) // 处理后的手风琴数据
const hasData = ref(false) // 是否有数据可以显示

// 获取稳定用户标识（参考GlobalPopup）
const getStableUserId = () => {
    if (isLoggedIn.value && userStore.userInfo?.id) {
        const currentId = userStore.userInfo.id.toString()
        const savedId = localStorage.getItem('stable_user_id')
        if (savedId && savedId !== currentId) {
            clearPopupState(savedId)
        }
        localStorage.setItem('stable_user_id', currentId)
        return currentId
    }
    const savedId = localStorage.getItem('stable_user_id')
    if (savedId && savedId !== 'anonymous') {
        return savedId
    }
    return 'anonymous'
}

// 清除弹窗状态
const clearPopupState = (oldUserId) => {
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
        if (key.includes(`list_popup_`) && key.includes(`_${oldUserId}_`)) {
            localStorage.removeItem(key)
        }
    })
}

// 清除未登录用户的所有会话存储键
const clearSessionStorageForUnloggedUser = () => {
    const userId = getStableUserId()
    const keys = Object.keys(sessionStorage)
    keys.forEach(key => {
        // 清除所有格式为 list_popup_visit_${popup.id}_${userId} 的键
        if (key.startsWith('list_popup_visit_') && key.endsWith(`_${userId}`)) {
            sessionStorage.removeItem(key)
        }
    })
}

// 检测页面刷新
const isPageRefresh = ref(false)
const detectPageRefresh = () => {
    const pageRefreshKey = 'page_refresh_detected'
    const wasRefreshed = localStorage.getItem(pageRefreshKey)
    if (wasRefreshed === 'true') {
        isPageRefresh.value = true
        localStorage.removeItem(pageRefreshKey)
        // 如果是未登录用户，清除所有会话存储中的弹窗相关键
        if (!isLoggedIn.value) {
            clearSessionStorageForUnloggedUser()
        }
    } else {
        isPageRefresh.value = false
    }
}

const handleBeforeUnload = () => {
    localStorage.setItem('page_refresh_detected', 'true')
}

// 判断是否应该显示弹窗（参考GlobalPopup的逻辑）
const shouldShowPopup = (popup) => {
    const today = new Date().toDateString()
    const userId = getStableUserId()

    // 检查用户是否选择了今日不再弹出（key必须包含弹窗ID确保唯一性）
    if (popup.id) {
        const noReminderKey = `list_popup_no_reminder_${popup.id}_${userId}_${today}`
        const noReminderToday = localStorage.getItem(noReminderKey)
        if (noReminderToday === 'true') {
            return false
        }
    }

    // 根据 popupGroup 判断用户状态是否匹配
    if (popup.popupGroup === 2 && !isLoggedIn.value) {
        return false // 登录用户弹窗，但用户未登录
    }
    if (popup.popupGroup === 1 && isLoggedIn.value) {
        return false // 未登录用户弹窗，但用户已登录
    }

    // 根据 model 判断弹窗时机（类似GlobalPopup的type）
    switch (popup.model) {
        case 1: // 每次登录或访问
            if (isLoggedIn.value) {
                // 已登录用户：检查是否已经为这次登录显示过
                if (popup.id) {
                    const loginVisitKey = `list_popup_login_visit_${popup.id}_${userId}`
                    const hasLoginVisited = localStorage.getItem(loginVisitKey)
                    if (!hasLoginVisited) {
                        return true
                    }
                }
                return false
            } else {
                // 未登录用户：每次访问（刷新页面或首次访问）都应该弹出
                // 对于未登录用户，每次刷新页面都应该弹出，使用 sessionStorage 标记当前会话
                // sessionStorage 在页面刷新后会清除，所以每次刷新都能弹出
                if (popup.id) {
                    const visitKey = `list_popup_visit_${popup.id}_${userId}`
                    const hasVisited = sessionStorage.getItem(visitKey)
                    // 如果当前会话还没有显示过，就显示
                    if (!hasVisited) {
                        return true
                    }
                } else {
                    // 如果没有 id，默认显示（首次访问）
                    return true
                }
                return false
            }

        case 2: // 每日一次
            if (popup.id) {
                const dailyKey = `list_popup_daily_${popup.id}_${userId}_${today}`
                const dailyShown = localStorage.getItem(dailyKey)
                return dailyShown !== 'true'
            }
            return false

        case 3: // 只弹一次
            if (popup.id) {
                const onceKey = `list_popup_once_${popup.id}_${userId}`
                const onceShown = localStorage.getItem(onceKey)
                return onceShown !== 'true'
            }
            return false

        case 4: // 每回大厅必弹
            return true

        default:
            return false
    }
}

// 保存弹窗显示状态
const savePopupShown = (popup) => {
    if (!popup || !popup.id) return

    const today = new Date().toDateString()
    const userId = getStableUserId()

    // "不再弹出"的key必须包含弹窗ID，确保唯一性
    if (isChecked.value) {
        const noReminderKey = `list_popup_no_reminder_${popup.id}_${userId}_${today}`
        localStorage.setItem(noReminderKey, 'true')
    }

    switch (popup.model) {
        case 1:
            if (isLoggedIn.value) {
                // 已登录用户：保存到 localStorage
                const loginVisitKey = `list_popup_login_visit_${popup.id}_${userId}`
                localStorage.setItem(loginVisitKey, 'true')
            } else {
                // 未登录用户：保存到 sessionStorage（刷新后清除）
                const visitKey = `list_popup_visit_${popup.id}_${userId}`
                sessionStorage.setItem(visitKey, 'true')
            }
            break
        case 2:
            const dailyKey = `list_popup_daily_${popup.id}_${userId}_${today}`
            localStorage.setItem(dailyKey, 'true')
            break
        case 3:
            const onceKey = `list_popup_once_${popup.id}_${userId}`
            localStorage.setItem(onceKey, 'true')
            break
        case 4:
            break
    }
}

// 获取并处理弹窗数据
const fetchAndShowPopups = async () => {
    try {
        const response = await GameApi.getAllListPopup()
        if (response && response.data) {
            // 过滤符合条件的弹窗
            const allPopups = response.data.filter(popup => {
                const switchStatusOk = popup.switchStatus
                const shouldShow = shouldShowPopup(popup)
                return switchStatusOk && shouldShow
            })

            if (allPopups.length > 0) {
                // 按照weight字段排序，weight值越大优先级越高
                const sortedPopups = allPopups.sort((a, b) => {
                    const weightA = a.weight || 0
                    const weightB = b.weight || 0
                    return weightB - weightA
                })

                popupListData.value = sortedPopups
                currentPopupIndex.value = 0
                // 显示第一个弹窗
                if (sortedPopups.length > 0) {
                    processPopupData(sortedPopups[0])
                    // 不自动显示，由优先级逻辑控制显示时机
                    // 只有在 visible 为 true 时才显示
                }
            } else {
                // 如果没有符合条件的弹窗（包括勾选了"不再弹出"），清空数据
                popupListData.value = []
                currentPopupIndex.value = 0
                processedAccordionData.value = []
                // 不在这里关闭弹窗，由 watch 中的逻辑处理
            }
        }
    } catch (error) {
        console.error('获取列表弹窗失败:', error)
        popupListData.value = []
        currentPopupIndex.value = 0
        processedAccordionData.value = []
    }
}

// 获取当前语言（使用i18n）
const getCurrentLanguage = () => {
    // 优先使用i18n的locale
    const currentLang = locale.value || localStorage.getItem('language') || 'en-US'
    // 处理语言代码映射：en-US -> en, tl-PH -> tl
    let mappedLang = currentLang
    if (currentLang === 'en-US') {
        mappedLang = 'en'
    }
    if (currentLang === 'tl-PH') {
        mappedLang = 'tl'
    }
    // 处理其他语言映射
    if (currentLang === 'pt-BR') {
        mappedLang = 'pt'
    }
    if (currentLang === 'hi-IN') {
        mappedLang = 'hi'
    }
    return { currentLang, mappedLang }
}

// 解析多语言数据（name、content可能是JSON字符串）
const parseMultilingualData = (dataStr) => {
    if (!dataStr) return ''

    try {
        // 如果已经是字符串且不是JSON格式，直接返回
        if (typeof dataStr === 'string' && !dataStr.startsWith('{')) {
            return dataStr
        }

        // 尝试解析为JSON
        if (typeof dataStr === 'string' && dataStr.startsWith('{')) {
            const parsedData = JSON.parse(dataStr)
            const { currentLang, mappedLang } = getCurrentLanguage()

            // 优先使用完整语言代码，然后尝试简化代码，最后使用en或tl作为fallback
            return parsedData[currentLang] || parsedData[mappedLang] || parsedData['en'] || parsedData['tl'] || parsedData[Object.keys(parsedData)[0]] || dataStr
        }

        // 如果已经是对象
        if (typeof dataStr === 'object') {
            const { currentLang, mappedLang } = getCurrentLanguage()
            return dataStr[currentLang] || dataStr[mappedLang] || dataStr['en'] || dataStr['tl'] || dataStr[Object.keys(dataStr)[0]] || ''
        }

        return dataStr
    } catch (error) {
        // 解析失败，直接返回原始字符串
        return dataStr
    }
}

// 获取图片URL（参考GlobalPopup）
const VITE_IMG_URL = import.meta.env.VITE_IMG_URL
const getImageUrl = (imagePath) => {
    if (!imagePath) return ''
    // 如果已经是完整的URL，直接返回
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath
    }
    // 使用环境变量拼接图片前缀
    if (VITE_IMG_URL) {
        return VITE_IMG_URL + imagePath
    }
    return imagePath
}

// 处理弹窗数据，转换为组件需要的格式
const processPopupData = (popup) => {
    currentPopupData.value = popup
    if (popup.popupInfoList && popup.popupInfoList.length > 0) {
        // 按照weight排序列表项
        const sortedItems = [...popup.popupInfoList].sort((a, b) => {
            const weightA = a.weight || 0
            const weightB = b.weight || 0
            return weightB - weightA
        })

        // 转换为组件需要的格式，解析多语言数据
        processedAccordionData.value = sortedItems.map(item => ({
            title: parseMultilingualData(item.name) || '',
            titleImage: getImageUrl(item.icon),
            content: parseMultilingualData(item.content) || '',
            unfold: item.unfold || false,
            jumpType: item.jumpType, // 跳转类型：1=内链，2=外链，不传=不跳转
            url: item.url || '' // 跳转地址
        }))

        // 找到默认展开的项
        const unfoldIndex = processedAccordionData.value.findIndex(item => item.unfold)
        expandedIndex.value = unfoldIndex >= 0 ? unfoldIndex : 0
    } else {
        processedAccordionData.value = []
    }
}

// 获取当前显示的列表数据（优先使用接口数据，否则使用props传入的数据）
const displayAccordionData = computed(() => {
    if (processedAccordionData.value.length > 0) {
        return processedAccordionData.value
    }
    return props.accordionData || []
})

// 处理跳转
const handleJump = (item) => {
    if (!item.url) return

    // 根据 jumpType 判断跳转方式
    switch (item.jumpType) {
        case 1: // 内链
            try {
                router.push(item.url)
            } catch (error) {
                window.location.href = item.url
            }
            break

        case 2: // 外链
            // 检查是否是完整的URL
            if (item.url.startsWith('http://') || item.url.startsWith('https://')) {
                window.open(item.url, '_blank')
            } else {
                // 如果不是完整URL，添加协议前缀
                const fullUrl = item.url.startsWith('//')
                    ? 'https:' + item.url
                    : 'https://' + item.url
                window.open(fullUrl, '_blank')
            }
            break

        default:
            // 不传jumpType或jumpType为其他值，不跳转
            break
    }
}

// 切换手风琴展开/收起
const toggleAccordion = async (index) => {
    if (expandedIndex.value === index) {
        // 如果点击的是已展开的项，可以选择收起或保持展开
        // 根据需求，这里保持展开状态（不收起）
        return
    } else {
        // 展开点击的项，收起其他的
        expandedIndex.value = index
        // 等待DOM更新后，强制重新渲染富文本内容
        await nextTick()
        renderKey.value++
    }
}

// 切换复选框状态
const toggleCheckbox = () => {
    isChecked.value = !isChecked.value
}

// 显示下一个弹窗
const showNextPopup = () => {
    currentPopupIndex.value++

    if (currentPopupIndex.value < popupListData.value.length) {
        // 还有下一个弹窗，继续显示
        const nextPopup = popupListData.value[currentPopupIndex.value]
        console.log('显示下一个弹窗:', {
            index: currentPopupIndex.value,
            popupId: nextPopup.id,
            popupName: nextPopup.name,
            total: popupListData.value.length
        })
        processPopupData(nextPopup)
        // 重置复选框状态
        isChecked.value = false
        return true
    } else {
        // 所有弹窗都显示完毕
        console.log('所有弹窗已显示完毕')
        return false
    }
}

// 处理关闭
const handleClose = () => {
    console.log('关闭弹窗:', {
        currentIndex: currentPopupIndex.value,
        total: popupListData.value.length,
        hasNext: currentPopupIndex.value + 1 < popupListData.value.length
    })

    // 保存当前弹窗的显示状态
    if (currentPopupData.value) {
        savePopupShown(currentPopupData.value)
    }

    emit('close', isChecked.value)

    // 检查是否还有下一个弹窗（当前索引+1是否小于总长度）
    if (currentPopupIndex.value + 1 < popupListData.value.length) {
        // 显示下一个弹窗
        if (showNextPopup()) {
            // 确保弹窗保持显示状态
            emit('update:visible', true)
            return // 不关闭弹窗，继续显示下一个
        }
    }

    // 没有更多弹窗，关闭弹窗
    console.log('关闭弹窗，没有更多弹窗')
    emit('update:visible', false)
}

// 处理遮罩层点击
const handleOverlayClick = () => {
    if (props.closeOnOverlay) {
        handleClose()
    }
}

// 防止背景滚动
const preventBodyScroll = () => {
    if (props.preventScroll) {
        document.body.style.overflow = 'hidden'
    }
}

const restoreBodyScroll = () => {
    if (props.preventScroll) {
        document.body.style.overflow = ''
    }
}

// 监听语言变化，重新处理弹窗数据
watch(() => locale.value, () => {
    if (currentPopupData.value) {
        // 语言变化时，重新处理当前弹窗数据
        processPopupData(currentPopupData.value)
    }
})

// 监听弹窗显示状态
watch(() => props.visible, async (newVal) => {
    if (newVal) {
        // 如果启用了自动获取，先获取数据检查是否有弹窗
        if (props.autoFetch) {
            // 先获取数据，检查是否有符合条件的弹窗
            await fetchAndShowPopups()
            // 检查是否有数据（包括处理后的数据）
            if (popupListData.value.length === 0 && processedAccordionData.value.length === 0) {
                hasData.value = false
                emit('update:visible', false)
                return
            }
            hasData.value = true
        } else {
            // 如果没有启用自动获取，检查是否有数据
            hasData.value = (props.accordionData && props.accordionData.length > 0)
            if (!hasData.value) {
                emit('update:visible', false)
                return
            }
        }

        preventBodyScroll()
        // 重置复选框状态
        isChecked.value = false
        // 如果启用了自动获取，数据已经在上面获取了
        if (!props.autoFetch) {
            // 使用props传入的数据
            const unfoldIndex = (props.accordionData || []).findIndex(item => item.unfold)
            expandedIndex.value = unfoldIndex >= 0 ? unfoldIndex : 0
        }
    } else {
        hasData.value = false
        restoreBodyScroll()
    }
}, { immediate: true })

onMounted(() => {
    detectPageRefresh()
    window.addEventListener('beforeunload', handleBeforeUnload)
    // 如果是未登录用户，每次组件挂载时都清除会话存储中的弹窗相关键
    // 这样每次刷新页面时，弹窗都可以重新显示
    if (!isLoggedIn.value) {
        clearSessionStorageForUnloggedUser()
    }
    if (props.visible) {
        preventBodyScroll()
    }
    // 不在这里自动获取数据，由 watch 中的逻辑处理
})

onUnmounted(() => {
    restoreBodyScroll()
    window.removeEventListener('beforeunload', handleBeforeUnload)
})
</script>

<style scoped>
.news-windows-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
}

.news-windows-container {
    width: 98%;
    max-width: 400px;
    max-height: 90vh;
    position: relative;
    display: flex;
    flex-direction: column;
}

.popup-frame {
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center;
    width: 100%;
    height: 600px;
    /* 固定高度，防止弹窗过长 */
    max-height: 90vh;
    position: relative;
    padding: 20px 10px 0 10px;
    padding-bottom: 70px;
    /* 为底部区域留出空间 */
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.accordion-list {
    /* 重要：允许 flex 子元素缩小，启用滚动 */
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
    overflow-x: hidden;
    margin-top: 100px;
    /* 限制最大高度，大约能显示5个项（1个展开150px + 4个收起60px + gap） */
    /* 隐藏滚动条但保留滚动功能 */
    scrollbar-width: none;
    /* Firefox */
    -ms-overflow-style: none;
    /* IE 和 Edge */
}

.accordion-list::-webkit-scrollbar {
    display: none;
    /* Chrome, Safari, Opera */
    width: 0;
}

.accordion-list::-webkit-scrollbar-track {
    background: transparent;
}

.accordion-list::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
}

.accordion-list::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
}

.accordion-item {
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.accordion-content {
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center;
    padding: 15px;
    box-sizing: border-box;
    transition: all 0.3s ease;
}

.accordion-content.expanded {
    min-height: 150px;
    max-height: 250px;
    height: auto;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.accordion-content.collapsed {
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.accordion-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    flex-shrink: 0;
}

.accordion-content.collapsed .accordion-header {
    margin-bottom: 0;
    width: 100%;
}

.title-image {
    width: 30px;
    height: 30px;
    object-fit: contain;
    flex-shrink: 0;
}

.accordion-title {
    font-size: 14px;
    font-weight: bold;
    margin: 0;
    background: linear-gradient(180deg, #fff71c 19.921875%, #ffba07 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    flex: 1;
    /* 最多显示2行，超出省略 */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    line-height: 1.4;
}

.accordion-body {
    margin-top: 10px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    /* 隐藏滚动条但保留滚动功能 */
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

.accordion-body::-webkit-scrollbar {
    width: 4px;
}

.accordion-body::-webkit-scrollbar-track {
    background: transparent;
}

.accordion-body::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
}

.accordion-body::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
}

.accordion-body.can-jump {
    cursor: pointer;
}

.accordion-text {
    color: #fff;
    font-size: 12px;
    line-height: 1.5;
    word-wrap: break-word;
    overflow: visible;
    /* 富文本内容中的图片样式 */
}

.accordion-text :deep(img) {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 8px 0;
    border-radius: 4px;
    object-fit: contain;
}

.accordion-text :deep(p) {
    margin: 8px 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.accordion-text :deep(div) {
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.accordion-text :deep(video) {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 8px 0;
    border-radius: 4px;
}

.accordion-text :deep(iframe) {
    max-width: 100%;
    width: 100%;
    height: auto;
    min-height: 200px;
    display: block;
    margin: 8px 0;
    border-radius: 4px;
}

.arrow-icon {
    width: 20px;
    height: 20px;
    object-fit: contain;
    flex-shrink: 0;
}

.bottom-section {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center;
    padding: 10px;
    box-sizing: border-box;
    z-index: 10;
    height: 70px;
    flex-shrink: 0;
}

.bottom-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 15px;
}

.checkbox-wrapper {
    display: flex;
    align-items: center;
    cursor: pointer;
    flex: 1;
}

.checkbox-box {
    width: 20px;
    height: 20px;
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
}

.check-icon {
    width: 14px;
    height: 14px;
    object-fit: contain;
}

.checkbox-text {
    color: #fff;
    font-size: 13px;
    white-space: nowrap;
    user-select: none;
    text-decoration: underline;
    font-weight: bold;
    letter-spacing: -0.5px;
}

.close-button-wrapper {
    flex-shrink: 0;
    cursor: pointer;
}

.close-button {
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center;
    min-width: 120px;
    display: flex;
    height: 38px;
    font-weight: bold;
    color: #fff;
    line-height: 38px;
    align-items: center;
    justify-content: center;
    text-align: center;
    box-sizing: border-box;
}

.close-text {
    color: #fff;
    font-size: 14px;
    font-weight: bold;
    text-align: center;
    white-space: nowrap;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .news-windows-container {
        width: 95%;
        max-width: 350px;
        max-height: 85vh;
    }

    .popup-frame {
        padding: 15px;
        padding-bottom: 70px;
        height: 550px;
        /* 固定高度 */
        max-height: 85vh;
    }

    .accordion-list {
        max-height: 380px;
        /* 移动端显示5个项（1个展开120px + 4个收起50px + gap） */
    }

    .accordion-content.expanded {
        min-height: 120px;
        max-height: 200px;
        padding: 12px;
    }

    .accordion-content.collapsed {
        min-height: 50px;
        padding: 12px;
    }

    .bottom-section {
        padding: 12px 15px;
    }

    .close-button {
        min-width: 100px;
        padding: 6px 15px;
    }
}
</style>
